import { CloseOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeviceGroupsIcon, DevicesIcon } from 'assets/images/svg';
import { buildDeviceOptionsWithIntegration } from 'library/helpers/integrationHelpers';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDevicesByGroupID } from 'reduxStore/assetGroups/selectors';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { selectDevices } from 'reduxStore/devices/selectors';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ManageDevicesToGroupModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, groupID }) => {
	const dispatch = useDispatch();
	const canUpdateGroup = useManagePermittedRoles('updateGroup');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		paddingLG,
		paddingXS,
		paddingSM,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		colorIcon,
		paddingXXS
	} = theme.useToken().token;

	const allDevices = useSelector(selectDevices);
	const groupDevices = useSelector((state) => selectDevicesByGroupID(state, groupID));
	const { loadingAddGroupAsset, loadingDeleteGroupAsset } = useSelector((state) => state.AssetGroups);

	const [newAssociatedDevices, setNewAssociatedDevices] = useState([]);
	const [removedAssociatedDevices, setRemovedAssociatedDevices] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	const availableDevices = useMemo(() => {
		const existing = groupDevices.map((d) => d.deviceID) || [];
		const filtered = existing.filter((id) => !removedAssociatedDevices.includes(id));
		const excluded = new Set([...filtered, ...newAssociatedDevices]);

		return buildDeviceOptionsWithIntegration(allDevices.filter((d) => !excluded.has(d.deviceID)));
	}, [allDevices, groupDevices, newAssociatedDevices, removedAssociatedDevices]);

	// ✅ Handle cancel and reset changes
	const handleCancel = () => {
		setNewAssociatedDevices([]);
		setRemovedAssociatedDevices([]);
		setHasChanges(false);
		setVisible(false);
	};

	// ✅ Add device
	const handleAddDevice = (deviceIDToAdd) => {
		if (removedAssociatedDevices.includes(deviceIDToAdd)) {
			const updatedRemovedDevices = removedAssociatedDevices.filter((id) => id !== deviceIDToAdd);
			setRemovedAssociatedDevices(updatedRemovedDevices);
			setHasChanges(newAssociatedDevices.length > 0 || updatedRemovedDevices.length > 0);
		} else {
			const updatedNewDevices = [...newAssociatedDevices, deviceIDToAdd];
			setNewAssociatedDevices(updatedNewDevices);
			setHasChanges(updatedNewDevices.length > 0 || removedAssociatedDevices.length > 0);
		}
	};

	// ✅ Remove device
	const handleRemoveDevice = (deviceIDToRemove) => {
		if (newAssociatedDevices.includes(deviceIDToRemove)) {
			const updatedNewDevices = newAssociatedDevices.filter((id) => id !== deviceIDToRemove);
			setNewAssociatedDevices(updatedNewDevices);
			setHasChanges(updatedNewDevices.length > 0 || removedAssociatedDevices.length > 0);
		} else {
			const updatedRemovedDevices = [...removedAssociatedDevices, deviceIDToRemove];
			setRemovedAssociatedDevices(updatedRemovedDevices);
			setHasChanges(newAssociatedDevices.length > 0 || updatedRemovedDevices.length > 0);
		}
	};

	// ✅ Submit changes
	const handleSubmit = () => {
		const devicesToAdd = newAssociatedDevices.filter((id) => !removedAssociatedDevices.includes(id));
		const devicesToRemove = removedAssociatedDevices.filter((id) => !newAssociatedDevices.includes(id));

		// Add devices to group
		if (devicesToAdd.length > 0) {
			dispatch(
				assetGroupActions.addGroupAsset({
					groupID,
					assetIDs: devicesToAdd
				})
			);
		}

		// Remove devices from group
		if (devicesToRemove.length > 0) {
			dispatch(
				assetGroupActions.removeGroupAsset({
					groupID,
					assetIDs: devicesToRemove
				})
			);
		}

		handleCancel();
	};

	const isLoading = loadingAddGroupAsset || loadingDeleteGroupAsset;

	return (
		<>
			<Tooltip title={!canUpdateGroup ? permissionDeniedMessage : 'Manage Devices'} placement='top'>
				<span onClick={(e) => canUpdateGroup && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<DeviceGroupsIcon color='currentColor' />}
							disabled={!canUpdateGroup}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={isLoading}>
								Cancel
							</Button>
							<Button onClick={handleSubmit} type='primary' loading={isLoading} disabled={!hasChanges}>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Devices
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Add or remove devices from this group.
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add Devices</Text>
							<Select
								showSearch
								placeholder='Select Devices'
								style={{ width: '100%' }}
								options={availableDevices}
								value={null}
								onSelect={handleAddDevice}
								filterOption={(input, option) =>
									(option?.searchLabel || option?.displayLabel || '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Existing Devices</Text>
							{groupDevices.filter((d) => !removedAssociatedDevices.includes(d.deviceID)).length === 0 &&
							newAssociatedDevices.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%',
										height: 85
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No devices in this group yet. Add devices using the dropdown above.'
									/>
								</Flex>
							) : (
								<Flex
									wrap='wrap'
									gap={paddingSM}
									align='flex-start'
									style={{
										height: 85,
										overflowY: 'auto',
										alignContent: 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{groupDevices
										.filter((device) => !removedAssociatedDevices.includes(device.deviceID))
										.map((device) => (
											<Tag
												key={`existing-${device.deviceID}`}
												color='gold'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<DevicesIcon color='currentColor' />
													{device.name}
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveDevice(device.deviceID)}
													/>
												</Flex>
											</Tag>
										))}
									{newAssociatedDevices.map((deviceID) => {
										const device = allDevices.find((d) => d.deviceID === deviceID);
										return (
											<Tag
												key={`new-${deviceID}`}
												color='gold'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<DevicesIcon color='currentColor' />
													{device?.name} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveDevice(deviceID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Flex>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ManageDevicesToGroupModal;
